import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_number: '',
    client_name: '',
    location: '',
    internal_reference: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, formData);
      toast.success('Project created!');
      setShowCreateModal(false);
      setFormData({ name: '', description: '', project_number: '', client_name: '', location: '', internal_reference: '' });
      fetchProjects();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const deleteProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This will permanently delete all project data including submittals, RFIs, budgets, change orders, and meeting minutes.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted successfully');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const exportToExcel = () => {
    import('xlsx').then(XLSX => {
      const exportData = projects.map(p => ({
        'Project #': p.project_number || 'N/A',
        'Name': p.name,
        'Client': p.client_name || 'N/A',
        'Location': p.location || 'N/A',
        'Internal Ref': p.internal_reference || 'N/A',
        'Description': p.description || 'N/A',
        'Created': new Date(p.created_at).toLocaleDateString()
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Projects');
      XLSX.writeFile(wb, 'projects_list.xlsx');
      toast.success('Exported to Excel');
    });
  };

  const exportToPDF = () => {
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then(({ default: autoTable }) => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text('Projects List', 14, 22);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
        
        autoTable(doc, {
          startY: 35,
          head: [['Project #', 'Name', 'Client', 'Location', 'Internal Ref']],
          body: projects.map(p => [
            p.project_number || 'N/A',
            p.name,
            p.client_name || 'N/A',
            p.location || 'N/A',
            p.internal_reference || 'N/A'
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [99, 102, 241] }
        });
        
        doc.save('projects_list.pdf');
        toast.success('Exported to PDF');
      });
    });
  };

  return (
    <div className="space-y-6" data-testid="projects-list-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-slate-900">Projects</h1>
          <p className="mt-2 text-base text-slate-600">Manage all your construction projects</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-slate-100 rounded-md p-1" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'card' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-card-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="view-list-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          <button onClick={exportToExcel} className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50" data-testid="export-excel-button">
            Export Excel
          </button>
          <button onClick={exportToPDF} className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50" data-testid="export-pdf-button">
            Export PDF
          </button>
          
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              data-testid="create-project-button"
            >
              Create Project
            </button>
          )}
        </div>
      </div>

      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow p-6 relative group"
              data-testid={`project-card-${project.id}`}
            >
              <div onClick={() => navigate(`/projects/${project.id}`)} className="cursor-pointer">
                {project.logo_url && (
                  <img src={project.logo_url} alt="" className="w-12 h-12 object-contain rounded mb-3" />
                )}
                <div className="flex items-center gap-2 mb-2">
                  {project.project_number && (
                    <span className="text-xs font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{project.project_number}</span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-1">{project.name}</h3>
                {project.client_name && <p className="text-sm text-indigo-600 font-medium">{project.client_name}</p>}
                {project.location && <p className="text-sm text-slate-500">{project.location}</p>}
                <p className="text-sm text-slate-600 mt-2">{project.description || 'No description'}</p>
                <div className="text-xs text-slate-400 mt-3">Created {new Date(project.created_at).toLocaleDateString()}</div>
              </div>
              {user?.role === 'admin' && (
                <button
                  onClick={(e) => deleteProject(project.id, project.name, e)}
                  className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 text-sm"
                  data-testid={`delete-project-${project.id}`}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
          <table className="w-full" data-testid="projects-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Project #</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Client</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Location</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Internal Ref</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Created</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} className="border-b hover:bg-slate-50" data-testid={`project-row-${project.id}`}>
                  <td className="p-4 font-mono text-sm text-indigo-600">{project.project_number || '-'}</td>
                  <td className="p-4">
                    <button onClick={() => navigate(`/projects/${project.id}`)} className="font-semibold text-slate-900 hover:text-indigo-600">
                      {project.name}
                    </button>
                    {project.description && <p className="text-xs text-slate-500 mt-1">{project.description}</p>}
                  </td>
                  <td className="p-4 text-sm">{project.client_name || '-'}</td>
                  <td className="p-4 text-sm">{project.location || '-'}</td>
                  <td className="p-4 text-sm font-mono">{project.internal_reference || '-'}</td>
                  <td className="p-4 text-sm text-slate-500">{new Date(project.created_at).toLocaleDateString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/projects/${project.id}`)} className="text-sm text-blue-600 hover:underline">View</button>
                      {user?.role === 'admin' && (
                        <button onClick={(e) => deleteProject(project.id, project.name, e)} className="text-sm text-red-600 hover:underline">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-project-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Create New Project</h2>
            <form onSubmit={createProject} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Project Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                    placeholder="e.g., Downtown Office Building"
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Project Number</label>
                  <input
                    type="text"
                    value={formData.project_number}
                    onChange={(e) => setFormData({...formData, project_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Auto-assigned if empty"
                    data-testid="project-number-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank for auto-generated number</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Internal Reference</label>
                  <input
                    type="text"
                    value={formData.internal_reference}
                    onChange={(e) => setFormData({...formData, internal_reference: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., INT-2024-001"
                    data-testid="project-internal-ref-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Client Name</label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., ABC Corporation"
                    data-testid="project-client-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., 123 Main Street, New York, NY"
                    data-testid="project-location-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows="3"
                    placeholder="Brief description of the project..."
                    data-testid="project-description-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700" data-testid="submit-project-button">
                  Create Project
                </button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50" data-testid="cancel-project-button">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsList;
